import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Edit2, Plus, Trash2, Search, Layers, Wand2, Search as SearchIcon, FlaskConical,
  Loader2, ExternalLink, CheckCircle2, Play,
} from 'lucide-react';
import { AggregationDefinition } from '../file_analytics/types';
import { isTrustedMessageOrigin } from '../../utils/trustedOrigins';
import Toast, { ToastMessage } from '../common/Toast';
import Button from '../common/Button';
import Modal from '../common/Modal';
import FormControl from '../common/FormControl';
import Skeleton from '../common/Skeleton';
import NoDataFound from '../common/NoDataFound';
import { confirmDelete } from '../common/confirm';
import RulesSection, { RulesSectionHandle } from '../file_analytics/sections/RulesSection';
import AggregationBuilderModal from '../file_analytics/modals/AggregationBuilderModal';
import { useCanDefineRules } from '../file_analytics/permissions';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';
import FileAnalyticsAPI from '../file_analytics/fileAnalyticsService';
import { CollectionType, DataSource } from '../file_analytics/types';
import useCmRunLock from '../file_analytics/useCmRunLock';
import CmPipelineEditorModal from './CmPipelineEditorModal';
import CmSchemaEditor, { CmSchemaEditorHandle } from './CmSchemaEditor';
import '../file_analytics/FileAnalytics.css';

interface CmRuleGovernanceProps {
  /** 'data_sources' = define data sources (file types, columns, cleaning);
   *  'rules' = pick data sources + define aggregations + rules;
   *  omitted = legacy combined view used by non-GRC dashboards. */
  section?: 'data_sources' | 'rules';
}

const CmRuleGovernance: React.FC<CmRuleGovernanceProps> = ({ section }) => {
  const canDefine = useCanDefineRules();
  const { allowed: canModify, loading: permLoading } = usePermission('CMG_RULES', 'view+modify');
  // While a run is in progress, changing a definition underneath it would leave
  // the run working from one description of the data and the screen showing
  // another. The server refuses such work anyway; this stops someone pressing a
  // button only to be told no.
  const { running: runInProgress, lockReason } = useCmRunLock();
  /** Editing is allowed only when permitted AND nothing is running. */
  const canEdit = canModify && !runInProgress;
  /** What to tell someone hovering a control they cannot currently use. */
  const editBlockedMsg = !canModify ? NOT_AUTHORISED_MSG : (lockReason ?? '');
  const { allowed: canView, loading: viewLoading } = usePermission('CMG_RULES', 'view');
  const isDataSources = section === 'data_sources';
  const isRules = section === 'rules';
  // Create/Edit/Delete is available in BOTH sections — a pipeline and a data source
  // are both collection types, so each section can add its own.
  const showSourceCrud = true;
  const noun = isRules ? 'pipeline' : 'data source';
  const Noun = isRules ? 'Pipeline' : 'Data source';
  const showSourceBlocks = !isRules;      // File Types / Columns / Cleaning
  const showRuleBlocks = !isDataSources;  // Data Sources picker / Aggregations / Rule Engine

  const [pipelines, setPipelines] = useState<CollectionType[]>([]);
  const [selected, setSelected] = useState<CollectionType | null>(null);
  // Data sources section — fan_data_source records (independent of CollectionType pipelines)
  const [sources, setSources] = useState<DataSource[]>([]);
  const [selectedSource, setSelectedSource] = useState<DataSource | null>(null);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<CollectionType | null>(null);
  const [editingSource, setEditingSource] = useState<DataSource | null>(null);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [navSearch, setNavSearch] = useState('');
  const autoSelectedRef = useRef(false);
  const schemaRef = useRef<CmSchemaEditorHandle>(null);
  const rulesRef = useRef<RulesSectionHandle>(null);
  // Aggregation builder — opened when user picks Aggregation card in source creation modal
  const [aggBuilderOpen, setAggBuilderOpen] = useState(false);
  // Aggregation Outputs panel (data-source section)
  const [sourceAggs, setSourceAggs] = useState<AggregationDefinition[]>([]);
  const [sourceAggsLoading, setSourceAggsLoading] = useState(false);
  const [sourceAggBuilderOpen, setSourceAggBuilderOpen] = useState(false);
  const [editingSourceAgg, setEditingSourceAgg] = useState<AggregationDefinition | null>(null);
  const [runningAggId, setRunningAggId] = useState<string | null>(null);
  // API source parameter editing (1. Parameters section)
  type ApiParam = { key: string; value: string; is_dynamic: boolean };
  const [apiParamsEdit, setApiParamsEdit] = useState<ApiParam[]>([]);
  const [savingParams, setSavingParams] = useState(false);
  // Graph source — OAuth connect
  const [graphConnecting, setGraphConnecting] = useState(false);
  const [graphConnected, setGraphConnected] = useState(false);
  const graphMsgListenerRef = useRef<((ev: MessageEvent) => void) | null>(null);
  let _apiParamId = 0;
  const mkApiParamId = () => `ap${++_apiParamId}`;

  // ── Data-source links (which data sources a pipeline uses) — rules section ──
  const [linkedIds, setLinkedIds] = useState<string[]>([]);
  const [draftIds, setDraftIds] = useState<string[]>([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [savingLinks, setSavingLinks] = useState(false);
  const [addOpen, setAddOpen] = useState(false);  // pipeline Data Sources "Add" picker
  const [pendingSourceId, setPendingSourceId] = useState('');

  const load = useCallback(async (): Promise<CollectionType[]> => {
    setLoading(true);
    try {
      const list = await FileAnalyticsAPI.listCollectionTypes();
      setPipelines(list);
      if (!autoSelectedRef.current && list.length > 0) {
        setSelected(list[0]);
        autoSelectedRef.current = true;
      }
      return list;
    } catch (err: any) {
      setToast({ type: 'error', message: err?.response?.data?.detail || 'Failed to load pipelines' });
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSources = useCallback(async (): Promise<DataSource[]> => {
    setLoading(true);
    try {
      const list = await FileAnalyticsAPI.listSources();
      setSources(list);
      if (!autoSelectedRef.current && list.length > 0) {
        setSelectedSource(list[0]);
        autoSelectedRef.current = true;
      }
      return list;
    } catch (err: any) {
      setToast({ type: 'error', message: err?.response?.data?.detail || 'Failed to load data sources' });
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canView) return;
    if (isDataSources) {
      loadSources();
    } else {
      load();
      // Also load data sources for the Rules section's "Data Sources" panel
      FileAnalyticsAPI.listSources().then(setSources).catch(() => setSources([]));
    }
  }, [isDataSources, load, loadSources, canView]);

  const loadSourceAggs = useCallback(async (sourceId: string) => {
    setSourceAggsLoading(true);
    try {
      const list = await FileAnalyticsAPI.listAggregations(undefined, sourceId);
      setSourceAggs(list);
    } catch {
      setSourceAggs([]);
    } finally {
      setSourceAggsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isDataSources && selectedSource) {
      loadSourceAggs(selectedSource.id);
    } else {
      setSourceAggs([]);
    }
  }, [isDataSources, selectedSource, loadSourceAggs]);

  // Sync local params editor when selected API source changes.
  useEffect(() => {
    setApiParamsEdit(selectedSource?.api_params ? [...selectedSource.api_params] : []);
  }, [selectedSource?.id]);

  // Load the selected pipeline's linked data sources (rules section only).
  useEffect(() => {
    if (!showRuleBlocks || !selected) return;
    let cancelled = false;
    setLoadingLinks(true);
    setAddOpen(false);
    FileAnalyticsAPI.getPipelineDataSources(selected.id)
      .then(rows => { if (!cancelled) { const ids = rows.map(r => r.id); setLinkedIds(ids); setDraftIds(ids); } })
      .catch(() => { if (!cancelled) { setLinkedIds([]); setDraftIds([]); } })
      .finally(() => { if (!cancelled) setLoadingLinks(false); });
    return () => { cancelled = true; };
  }, [showRuleBlocks, selected]);

  const openCreate = () => {
    if (isDataSources) { setEditingSource(null); } else { setEditing(null); }
    setEditorOpen(true);
  };
  const openEdit = () => {
    if (isDataSources) { if (selectedSource) { setEditingSource(selectedSource); setEditorOpen(true); } }
    else { if (selected) { setEditing(selected); setEditorOpen(true); } }
  };

  const handleSaved = (ct: CollectionType) => {
    setEditorOpen(false);
    setToast({ type: 'success', message: `${Noun} "${ct.name}" saved` });
    load().then(() => setSelected(ct));
  };

  const handleSourceSaved = (ds: DataSource) => {
    setEditorOpen(false);
    setToast({ type: 'success', message: `Data source "${ds.name}" saved` });
    loadSources().then(() => { setSelectedSource(ds); });
  };

  const connectGraphAccount = async () => {
    if (!selectedSource) return;
    setGraphConnecting(true);
    try {
      const authorizeUrl = await FileAnalyticsAPI.getGraphAuthorizeUrl(Number(selectedSource.id));
      const popup = window.open(authorizeUrl, 'ms_graph_auth', 'width=520,height=620,left=200,top=100');
      if (!popup) {
        setToast({ type: 'error', message: 'Could not open popup. Allow popups for this page.' });
        setGraphConnecting(false);
        return;
      }
      const onMsg = (ev: MessageEvent) => {
        // ANYGRC_Vulnerabilities: "postMessage Origin Not Validated in OAuth
        // Popup Handlers" — reject messages from an untrusted origin before
        // acting on the payload.
        if (!isTrustedMessageOrigin(ev.origin)) return;
        if (ev.data?.type !== 'graph_oauth') return;
        window.removeEventListener('message', onMsg);
        graphMsgListenerRef.current = null;
        popup.close();
        setGraphConnecting(false);
        if (ev.data.success) {
          setGraphConnected(true);
          setToast({ type: 'success', message: 'Microsoft account connected.' });
        } else {
          setToast({ type: 'error', message: 'Microsoft account connection failed. Try again.' });
        }
      };
      graphMsgListenerRef.current = onMsg;
      window.addEventListener('message', onMsg);
    } catch {
      setToast({ type: 'error', message: 'Failed to start Microsoft login.' });
      setGraphConnecting(false);
    }
  };

  const saveApiParams = async () => {
    if (!selectedSource) return;
    setSavingParams(true);
    try {
      const saved = await FileAnalyticsAPI.updateSource(selectedSource.id, {
        name: selectedSource.name,
        api_params: apiParamsEdit,
      });
      setSelectedSource(saved);
      setToast({ type: 'success', message: 'Parameters saved' });
    } catch (err: any) {
      setToast({ type: 'error', message: err?.response?.data?.detail || 'Save failed' });
    } finally {
      setSavingParams(false);
    }
  };

  const handleDeleteSource = async (): Promise<boolean> => {
    if (!selectedSource) return false;
    const ok = await confirmDelete({
      text: `Delete data source "${selectedSource.name}"? Its datasets and cleaning recipes become inaccessible.`,
    });
    if (!ok) return false;
    try {
      await FileAnalyticsAPI.deleteSource(selectedSource.id);
      setToast({ type: 'delete', message: `Data source "${selectedSource.name}" deleted` });
      autoSelectedRef.current = false;
      setSelectedSource(null);
      await loadSources();
      return true;
    } catch (err: any) {
      setToast({ type: 'error', message: err?.response?.data?.detail || 'Delete failed' });
      return false;
    }
  };

  const handleDeletePipeline = async (): Promise<boolean> => {
    if (!selected) return false;
    const ok = await confirmDelete({
      text: `Delete ${noun} "${selected.name}"? Its columns, cleaning, aggregations and rules become inaccessible.`,
    });
    if (!ok) return false;
    try {
      await FileAnalyticsAPI.deleteCollectionType(selected.id);
      setToast({ type: 'delete', message: `${Noun} "${selected.name}" deleted` });
      autoSelectedRef.current = false;
      setSelected(null);
      await load();
      return true;
    } catch (err: any) {
      setToast({ type: 'error', message: err?.response?.data?.detail || 'Delete failed' });
      return false;
    }
  };

  const toggleLink = (id: string) =>
    setDraftIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const linksDirty = draftIds.slice().sort().join(',') !== linkedIds.slice().sort().join(',');

  const saveLinks = async (idsArg?: string[]) => {
    if (!selected) return;
    // React state (draftIds) updates async — reading it right after a toggleLink()
    // in the same handler returns the STALE value, which PUT-wiped just-added
    // bindings (the "data source disappears on reload" bug). Callers that mutate the
    // set this tick pass the explicit next-ids; otherwise fall back to draftIds.
    const ids = idsArg ?? draftIds;
    setSavingLinks(true);
    try {
      await FileAnalyticsAPI.setPipelineDataSources(selected.id, ids);
      setLinkedIds(ids);
      setToast({ type: 'success', message: 'Data sources updated' });
    } catch (err: any) {
      // Backend rejects removing a source still used by a rule/aggregation (409) —
      // restore the list to the last-saved set so the blocked source reappears.
      setDraftIds(linkedIds);
      setToast({ type: 'error', message: err?.response?.data?.detail || 'Failed to save data sources' });
    } finally {
      setSavingLinks(false);
    }
  };

  const runSourceAgg = async (agg: AggregationDefinition) => {
    if (runningAggId) return;
    setRunningAggId(agg.id);
    try {
      await FileAnalyticsAPI.runAggregation(agg.id);
      setToast({ type: 'success', message: `Aggregation "${agg.name}" started` });
      if (selectedSource) loadSourceAggs(selectedSource.id);
    } catch (err: any) {
      setToast({ type: 'error', message: err?.response?.data?.detail || 'Failed to run aggregation' });
    } finally {
      setRunningAggId(null);
    }
  };

  const deleteSourceAgg = async (agg: AggregationDefinition) => {
    const ok = await confirmDelete({ title: `Delete aggregation "${agg.name}"?` });
    if (!ok) return;
    try {
      await FileAnalyticsAPI.deleteAggregation(agg.id);
      setToast({ type: 'success', message: 'Aggregation deleted' });
      if (selectedSource) loadSourceAggs(selectedSource.id);
    } catch (err: any) {
      setToast({ type: 'error', message: err?.response?.data?.detail || 'Failed to delete aggregation' });
    }
  };

  const allowList = isDataSources
    ? (selectedSource?.allowed_source_types ?? ['csv', 'xlsx', 'xls', 'txt'])
    : (selected?.allowed_source_types ?? ['pdf', 'xlsx', 'xls', 'csv', 'txt']);
  // Rules section: data sources panel shows real fan_data_source records (from sources state)
  const availableSources = sources;
  const linkedSources = availableSources.filter(p => draftIds.includes(p.id));
  const navPipelines = pipelines.filter(
    p => !navSearch || p.name.toLowerCase().includes(navSearch.toLowerCase()),
  );

  const leftHeading = isRules ? 'Pipelines' : isDataSources ? 'Data Sources' : 'Data Pipelines';
  const emptyHint = isRules
    ? 'Select a pipeline to choose its data sources and define aggregations and rules.'
    : isDataSources
      ? 'Select a data source to define its accepted file types, columns and cleaning.'
      : 'Select a pipeline to define its sources, columns, cleaning and rules.';
  const noneHint = showSourceCrud
    ? 'Click "Create" to add one.'
    : 'No data sources yet — create one under the Data Sources section first.';

  /** Collapsible l1 panel. Heavy bodies (editors/sections) mount only when open. */
  /** Static section (no accordion) — a heading row with a thin full-width rule beneath it,
   *  then the body, always visible. Sections are differentiated by the divider line. */
  const ROMAN: Record<string, string> = {
    filetypes: 'I', columns: 'II', cleaning: 'III', aggregations: 'IV',
    datasources: 'I', rules: 'II',
  };
  const panel = (
    id: string,
    title: string,
    _sub: string,   // subtext intentionally not rendered — headings only (per design)
    count: React.ReactNode,
    headerRight: React.ReactNode,
    body: React.ReactNode,
    bodyPadding?: string,
  ) => (
    <section
      data-testid={`cm-panel-${id}`}
      style={{ border: '2px solid var(--brand-light-strong)', borderRadius: 8, overflow: 'hidden' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', background: 'var(--brand-subtle)', borderBottom: '2px solid var(--brand-light-strong)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 600, color: 'var(--text-heading)' }}>
          {ROMAN[id] && (
            <span style={{
              width: 20, height: 20, borderRadius: '50%', background: 'var(--primary, #4f46e5)',
              color: '#fff', fontSize: 11, fontWeight: 700,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>{ROMAN[id]}</span>
          )}
          {title}
        </span>
        {count != null && <span className="collapse-section__score-pill">{count}</span>}
        <span style={{ flex: 1 }} />
        {headerRight}
      </div>
      <div style={{ padding: bodyPadding ?? '12px' }}>{body}</div>
    </section>
  );

  if (!viewLoading && !canView) {
    return (
      <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
        <NoDataFound title="Not authorised" message={`You do not have access to ${isDataSources ? 'data sources' : 'monitoring pipelines'}.`} />
      </div>
    );
  }

  return (
    <div
      className="flex h-full overflow-hidden"
      data-testid={isDataSources ? 'cm-datasources-root' : 'cm-rule-governance-root'}
    >
        {/* ── Left nav card: picker ─────────────────────────────────────── */}
        <div className="netbody-card side-nav-card side-nav-card--fixed">
          <div className="side-nav-card-heading flex items-center justify-between">
            <span>{leftHeading}</span>
            {showSourceCrud && (
              <Button variant="fill" icon={Plus} onClick={openCreate} disabled={permLoading || !canEdit} aria-label="Create" title={!canEdit ? editBlockedMsg : 'Create'} data-testid="cm-pipeline-create-btn" />
            )}
          </div>

          <div className="side-nav-card-search">
            <FormControl.Input
              type="text"
              placeholder={`Search ${leftHeading.toLowerCase()}…`}
              value={navSearch}
              onChange={(e: any) => setNavSearch(e.target.value)}
              leadingIcon={<Search size={13} />}
              className="fg--no-margin"
              data-testid="INP_cm_nav_search"
            />
          </div>

          <nav aria-label={leftHeading}>
            {loading && [0, 1, 2, 3, 4, 5].map(i => {
              const nameWidths = ['72%', '55%', '84%', '48%', '66%', '60%'];
              return (
                <div key={i} className="side-panel-menu-item" style={{ pointerEvents: 'none' }} aria-hidden="true">
                  <Skeleton.Circle size={14} />
                  <Skeleton.Box width={nameWidths[i]} height={11} style={{ flex: '0 1 auto' }} />
                </div>
              );
            })}

            {!loading && (isDataSources ? sources.length === 0 : pipelines.length === 0) && (
              <NoDataFound title={isDataSources ? 'No data sources' : 'No pipelines'} message={noneHint} icon={<Layers size={28} />} />
            )}

            {!loading && !isDataSources && pipelines.length > 0 && navPipelines.length === 0 && (
              <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--text-muted)' }}>No matches.</div>
            )}

            {/* Data Sources section — renders fan_data_source records */}
            {!loading && isDataSources && sources
              .filter(s => !navSearch || s.name.toLowerCase().includes(navSearch.toLowerCase()))
              .map(ds => {
                const active = selectedSource?.id === ds.id;
                return (
                  <button
                    key={ds.id}
                    type="button"
                    onClick={() => setSelectedSource(ds)}
                    className={`side-panel-menu-item${active ? ' side-panel-menu-item--active' : ''}`}
                    data-testid={`cm-source-row-${ds.id}`}
                    title={ds.name}
                  >
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
                      {ds.name}
                    </span>
                  </button>
                );
              })}

            {/* Pipelines / Rules section — renders fan_collection_type records */}
            {!loading && !isDataSources && navPipelines.map(ct => {
              const active = selected?.id === ct.id;
              const count = ct.rule_count ?? 0;
              return (
                <button
                  key={ct.id}
                  type="button"
                  onClick={() => setSelected(ct)}
                  className={`side-panel-menu-item${active ? ' side-panel-menu-item--active' : ''}`}
                  data-testid={`cm-pipeline-row-${ct.id}`}
                  title={ct.name}
                >
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
                    {ct.name}
                  </span>
                  {showRuleBlocks && (
                    <span
                      style={{
                        flexShrink: 0, fontSize: 11, fontWeight: 600, minWidth: 20, textAlign: 'center',
                        padding: '1px 6px', borderRadius: 9999,
                        background: active ? 'var(--interactive-primary-bg)' : 'var(--border-subtle)',
                        color: active ? '#fff' : 'var(--text-tertiary)',
                      }}
                      title={`${count} rule${count === 1 ? '' : 's'}`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="netbody-card side-content-card side-content-card--fixed cm-rg-content flex flex-col flex-1 min-w-0">
          {(isDataSources ? !selectedSource : !selected) ? (
            <div className="fa-main-pane-empty">
              <div style={{ fontSize: 14, color: 'var(--text-muted)', textAlign: 'center' }}>
                {loading ? 'Loading…' : emptyHint}
              </div>
            </div>
          ) : (
            <>
              {/* ── Header: name + Edit button on the right ───────────────── */}
              <div className="netbody-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>
                  {isRules ? 'Name of Pipeline' : 'Name of Data Source'} : "{isDataSources ? selectedSource!.name : selected!.name}"
                </span>
                {showSourceCrud && (
                  <button
                    type="button"
                    className="cm-edit-icon"
                    onClick={openEdit}
                    disabled={permLoading || !canEdit}
                    title={!canEdit ? editBlockedMsg : `Edit ${isRules ? 'pipeline' : 'data source'}`}
                    aria-label="Edit"
                    data-testid="BTN_cm_pipeline_edit"
                  >
                    <Edit2 size={16} />
                  </button>
                )}
              </div>

              {/* ── Single page: collapsible panels (no tabs) ─────────────── */}
              <div className="cm-rg-singlepage">
                <div className="collapse-section-stack" style={{ padding: '0 14px' }}>
                  {showSourceBlocks && (
                    <>
                      {/* ── I. Source Details card — always shown for every data source ── */}
                      {(() => {
                        const ds = selectedSource!;
                        const typeLabels: Record<string, { label: string; color: string }> = {
                          normal:      { label: 'File Upload',         color: '#4f46e5' },
                          api:         { label: 'REST / JSON API',     color: '#0891b2' },
                          graph:       { label: 'Microsoft Graph',     color: '#0369a1' },
                          combined:    { label: 'Combined Dataset',    color: '#7c3aed' },
                          aggregation: { label: 'Aggregation',         color: '#059669' },
                        };
                        const typeInfo = typeLabels[ds.source_type ?? 'normal'] ?? typeLabels.normal;
                        return (
                          <section
                            data-testid="cm-panel-sourcedetails"
                            style={{ border: '2px solid var(--brand-light-strong)', borderRadius: 8, overflow: 'hidden', marginBottom: 2 }}
                          >
                            {/* Header */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', background: 'var(--brand-subtle)', borderBottom: '2px solid var(--brand-light-strong)' }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 600, color: 'var(--text-heading)', flex: 1 }}>
                                <span style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--primary,#4f46e5)', color: '#fff', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>I</span>
                                Source Details
                              </span>
                              {showSourceCrud && canModify && (
                                <button
                                  type="button"
                                  className="cm-edit-icon"
                                  onClick={openEdit}
                                  disabled={runInProgress}
                                  title={runInProgress ? (lockReason ?? '') : 'Edit data source'}
                                  aria-label="Edit data source"
                                >
                                  <Edit2 size={14} />
                                </button>
                              )}
                            </div>
                            {/* Body */}
                            <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                              {/* Type badge + description row */}
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: `${typeInfo.color}18`, color: typeInfo.color, flexShrink: 0, marginTop: 1 }}>
                                  {typeInfo.label}
                                </span>
                                {ds.description ? (
                                  <span style={{ fontSize: 13, color: 'var(--text-body)', lineHeight: 1.5 }}>{ds.description}</span>
                                ) : (
                                  <span style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>No description</span>
                                )}
                              </div>

                              {/* API details */}
                              {(ds.source_type === 'api' || ds.source_type === 'graph') && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 14px', alignItems: 'baseline' }}>
                                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                    {ds.source_type === 'graph' ? 'Folder path' : 'Endpoint'}
                                  </span>
                                  <span style={{ fontSize: 13, color: 'var(--text-heading)', wordBreak: 'break-all', fontFamily: 'monospace' }}>
                                    {ds.api_endpoint || '—'}
                                  </span>
                                  {ds.source_type === 'api' && (<>
                                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Authenticator</span>
                                    <span style={{ fontSize: 13, color: 'var(--text-heading)' }}>
                                      {ds.authenticator_name
                                        ? <>{ds.authenticator_name}{' '}<span style={{ fontSize: 11, color: ds.authenticator_session_valid ? '#15803d' : '#dc2626', fontWeight: 600 }}>{ds.authenticator_session_valid ? '● connected' : '● session expired'}</span></>
                                        : ds.api_auth_type
                                          ? <span style={{ textTransform: 'capitalize' }}>{ds.api_auth_type}</span>
                                          : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>None / public</span>
                                      }
                                    </span>
                                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Refresh mode</span>
                                    <span style={{ fontSize: 13, color: 'var(--text-heading)', textTransform: 'capitalize' }}>{ds.collection_mode ?? 'accumulated'}</span>
                                  </>)}
                                </div>
                              )}

                              {/* Aggregation / Combined resolution */}
                              {(ds.source_type === 'aggregation' || ds.source_type === 'combined') && (
                                <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                  {ds.source_type === 'aggregation'
                                    ? 'This source is a group-by aggregation computed from another source. Configure the aggregation from the Pipelines tab.'
                                    : 'This source is a side-by-side combination of two existing sources. Refresh re-joins them automatically.'
                                  }
                                </div>
                              )}

                              {/* Snapshot status */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', borderTop: '1px solid var(--border-color,#e5e7eb)', paddingTop: 8, marginTop: 2 }}>
                                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                  Last run:{' '}
                                  <strong style={{ color: 'var(--text-heading)' }}>
                                    {ds.saved_at ? new Date(ds.saved_at).toLocaleString() : 'Never'}
                                  </strong>
                                </span>
                                {ds.saved_row_count != null && (
                                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                    Rows: <strong style={{ color: 'var(--text-heading)' }}>{ds.saved_row_count.toLocaleString()}</strong>
                                  </span>
                                )}
                              </div>
                            </div>
                          </section>
                        );
                      })()}

                      {/* 2. Source Configuration (API params or Graph connect — rendered separately below) */}
                      {selectedSource?.source_type === 'graph' && (
                        <section
                          data-testid="cm-panel-graphconfig"
                          style={{ border: '2px solid var(--brand-light-strong)', borderRadius: 8, overflow: 'hidden' }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', background: 'var(--brand-subtle)', borderBottom: '2px solid var(--brand-light-strong)' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 600, color: 'var(--text-heading)' }}>
                              <span style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--primary, #4f46e5)', color: '#fff', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>1</span>
                              Source Configuration
                            </span>
                          </div>
                          <div style={{ padding: '12px' }}>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Folder path</div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-heading)', marginBottom: 14, wordBreak: 'break-all' }}>
                              {(selectedSource as any).api_endpoint || '—'}
                            </div>
                            {graphConnected ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#15803d', fontWeight: 600, fontSize: 13 }}>
                                <CheckCircle2 size={16} /> Microsoft account connected
                              </div>
                            ) : (
                              <Button
                                variant="fill"
                                icon={graphConnecting ? Loader2 : ExternalLink}
                                onClick={connectGraphAccount}
                                disabled={graphConnecting}
                              >
                                {graphConnecting ? 'Opening Microsoft login…' : 'Connect Microsoft Account'}
                              </Button>
                            )}
                          </div>
                        </section>
                      )}
                      {selectedSource?.source_type === 'api' ? (
                        <section
                          data-testid="cm-panel-apiparams"
                          style={{ border: '2px solid var(--brand-light-strong)', borderRadius: 8, overflow: 'hidden' }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', background: 'var(--brand-subtle)' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 600, color: 'var(--text-heading)' }}>
                              <span style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--primary, #4f46e5)', color: '#fff', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>1</span>
                              Parameters
                            </span>
                            <span className="collapse-section__score-pill" style={{ flexShrink: 0 }}>
                              {apiParamsEdit.length} param{apiParamsEdit.length === 1 ? '' : 's'}
                            </span>
                            {(
                              <button type="button" className="cm-edit-icon" style={{ marginLeft: 'auto' }}
                                onClick={() => setApiParamsEdit(prev => [...prev, { key: '', value: '', is_dynamic: false }])}
                                title="Add parameter" aria-label="Add parameter">
                                <Plus size={14} />
                              </button>
                            )}
                          </div>
                          {true && (
                            <div style={{ padding: '8px 12px' }}>
                              {apiParamsEdit.length > 0 && (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: '5px 8px', marginBottom: 8, alignItems: 'center' }}>
                                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Key</span>
                                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Value</span>
                                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Dynamic</span>
                                  <span />
                                  {apiParamsEdit.map((p, i) => (
                                    <React.Fragment key={i}>
                                      <input className="fi-input" placeholder="key" value={p.key}
                                        onChange={e => setApiParamsEdit(prev => prev.map((x, j) => j === i ? { ...x, key: e.target.value } : x))} />
                                      <input className="fi-input" placeholder={p.is_dynamic ? '(asked at refresh)' : 'value'} value={p.value}
                                        disabled={p.is_dynamic}
                                        onChange={e => setApiParamsEdit(prev => prev.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} />
                                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                                        <input type="checkbox" checked={p.is_dynamic}
                                          title="Ask for this value at refresh time"
                                          onChange={e => setApiParamsEdit(prev => prev.map((x, j) => j === i ? { ...x, is_dynamic: e.target.checked, value: e.target.checked ? '' : x.value } : x))} />
                                      </div>
                                      <button type="button" onClick={() => setApiParamsEdit(prev => prev.filter((_, j) => j !== i))}
                                        style={{ color: 'var(--red-600)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                                        <Trash2 size={13} />
                                      </button>
                                    </React.Fragment>
                                  ))}
                                </div>
                              )}
                              {apiParamsEdit.length === 0 && (
                                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>No parameters yet. Click + to add query params sent with every request.</p>
                              )}
                              <Button variant="fill" onClick={saveApiParams} disabled={savingParams || !canEdit}
                                style={{ marginTop: 4 }}>
                                {savingParams ? 'Saving…' : 'Save Parameters'}
                              </Button>
                            </div>
                          )}
                        </section>
                      ) : null}

                      {panel(
                        'columns', 'Columns', 'the schema every upload must match',
                        (() => { const n = (isDataSources ? selectedSource : selected)?.standard_schema?.length ?? 0; return `${n} column${n === 1 ? '' : 's'}`; })(),
                        canDefine ? (
                          <button type="button" className="cm-edit-icon" onClick={() => schemaRef.current?.edit()} title="Edit" aria-label="Edit" data-testid="BTN_cm_schema_edit"><Edit2 size={16} /></button>
                        ) : null,
                        isDataSources ? (
                          <CmSchemaEditor
                            ref={schemaRef}
                            key={`schema-ds-${selectedSource!.id}`}
                            source={selectedSource!}
                            readOnly={!canDefine}
                            onSourceSaved={(ds) => { loadSources().then(() => setSelectedSource(ds)); }}
                          />
                        ) : (
                          <CmSchemaEditor
                            ref={schemaRef}
                            key={`schema-${selected!.id}`}
                            pipeline={selected!}
                            readOnly={!canDefine}
                            onSaved={(ct) => { load().then(() => setSelected(ct)); }}
                          />
                        ),
                      )}

                      {/* The preparation rules used to be written here, inside
                          whichever data set they belonged to. They are not a list
                          per data set: they carry one sequence for the whole
                          entity and the run follows that sequence, so reading
                          them here hid the only thing that decides what happens
                          first. They now live under Preprocessing as one ordered
                          list, with the data set chosen on the rule itself. */}

                      {isDataSources && panel(
                        'aggregations', 'Aggregation Outputs', 'define aggregation outputs from this data source',
                        sourceAggs.length > 0 ? sourceAggs.length : null,
                        canDefine ? (
                          <button
                            type="button"
                            className="cm-edit-icon"
                            onClick={() => { setEditingSourceAgg(null); setSourceAggBuilderOpen(true); }}
                            title="Add aggregation"
                            aria-label="Add aggregation"
                            data-testid="BTN_cm_source_agg_add"
                          >
                            <Plus size={16} />
                          </button>
                        ) : null,
                        sourceAggsLoading ? (
                          <div className="collapse-section__skeleton">
                            <Skeleton.Box height={12} width="70%" />
                            <Skeleton.Box height={12} width="50%" />
                          </div>
                        ) : sourceAggs.length === 0 ? (
                          <p className="collapse-section__empty">
                            No aggregation outputs defined{canDefine ? ' — click + to create one.' : '.'}
                          </p>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {sourceAggs.map(agg => (
                              <div
                                key={agg.id}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 8,
                                  padding: '7px 10px',
                                  borderRadius: 6,
                                  border: '1px solid var(--brand-light-strong)',
                                  background: 'var(--surface-subtle, var(--bg-card))',
                                }}
                              >
                                <span style={{ flex: 1, minWidth: 0 }}>
                                  <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-heading)', marginRight: 8 }}>
                                    {agg.name}
                                  </span>
                                  <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    {agg.type}
                                  </span>
                                </span>
                                <span
                                  style={{
                                    fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 99,
                                    background: agg.status === 'Ready' ? 'var(--success-subtle, #d1fae5)' : agg.status === 'Failed' ? 'var(--danger-subtle, #fee2e2)' : 'var(--brand-subtle)',
                                    color: agg.status === 'Ready' ? 'var(--success, #059669)' : agg.status === 'Failed' ? 'var(--danger, #dc2626)' : 'var(--text-muted)',
                                  }}
                                >
                                  {agg.status}
                                </span>
                                {agg.last_run_at && (
                                  <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                    {new Date(agg.last_run_at).toLocaleDateString()}
                                  </span>
                                )}
                                <button
                                  type="button"
                                  className="cm-edit-icon"
                                  title={runInProgress ? (lockReason ?? '') : 'Run aggregation'}
                                  aria-label="Run aggregation"
                                  disabled={runningAggId === agg.id || runInProgress}
                                  onClick={() => runSourceAgg(agg)}
                                  data-testid={`BTN_cm_source_agg_run_${agg.id}`}
                                >
                                  {runningAggId === agg.id
                                    ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                                    : <Play size={14} />}
                                </button>
                                {canDefine && (
                                  <>
                                    <button
                                      type="button"
                                      className="cm-edit-icon"
                                      title="Edit aggregation"
                                      aria-label="Edit aggregation"
                                      onClick={() => { setEditingSourceAgg(agg); setSourceAggBuilderOpen(true); }}
                                      data-testid={`BTN_cm_source_agg_edit_${agg.id}`}
                                    >
                                      <Edit2 size={14} />
                                    </button>
                                    <button
                                      type="button"
                                      className="cm-edit-icon"
                                      title={runInProgress ? (lockReason ?? '') : 'Delete aggregation'}
                                      aria-label="Delete aggregation"
                                      disabled={runInProgress}
                                      onClick={() => deleteSourceAgg(agg)}
                                      data-testid={`BTN_cm_source_agg_delete_${agg.id}`}
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        ),
                      )}

                    </>
                  )}

                  {showRuleBlocks && (
                    <>
                      {panel(
                        'datasources', 'Data Sources', '',
                        draftIds.length,
                        (
                          <Button
                            variant="secondary-outline"
                            icon={Plus}
                            onClick={() => { setPendingSourceId(''); setAddOpen(true); }}
                            disabled={permLoading || !canEdit || savingLinks}
                            aria-label="Add data source"
                            title={!canEdit ? editBlockedMsg : 'Add data source'}
                            data-testid="BTN_cm_pipeline_sources_add"
                          />
                        ),
                        loadingLinks ? (
                          <div className="collapse-section__skeleton">
                            <Skeleton.Box height={12} width="60%" />
                            <Skeleton.Box height={12} width="80%" />
                          </div>
                        ) : (
                          <>
                            {/* Activated (linked) data sources only */}
                            {linkedSources.length === 0 ? (
                              <p className="collapse-section__empty">
                                No data sources added yet{canDefine ? ' — use “Add” to link one.' : '.'}
                              </p>
                            ) : (
                              <div>
                                {linkedSources.map((ds, idx) => {
                                  const isLast = linkedSources.length <= 1;
                                  return (
                                  <div
                                    key={ds.id}
                                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 6px', borderRadius: 6 }}
                                    data-testid={`cm-pipeline-source-${ds.id}`}
                                  >
                                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)', minWidth: 22, textAlign: 'right' }}>{idx + 1}.</span>
                                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{ds.name}</span>
                                    {canDefine && (
                                      <button
                                        type="button"
                                        className="btn-trash-soft"
                                        disabled={permLoading || !canEdit || isLast}
                                        title={!canEdit ? editBlockedMsg : (isLast ? 'At least one data source is required' : 'Remove')}
                                        style={(isLast || !canEdit) ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                                        onClick={() => { if (!isLast && canEdit) toggleLink(ds.id); }}
                                        data-testid={`BTN_cm_pipeline_source_remove_${ds.id}`}
                                      >
                                        <Trash2 size={13} />
                                      </button>
                                    )}
                                  </div>
                                  );
                                })}
                              </div>
                            )}
                          </>
                        ),
                      )}

                      {panel(
                        'rules', 'Detection Rules', 'detection rules run on this pipeline',
                        selected!.rule_count ?? 0,
                        (
                          <Button variant="fill" icon={Plus} disabled={permLoading || !canEdit || linkedSources.length === 0} onClick={() => rulesRef.current?.openCreate()} aria-label="New detection rule" title={!canEdit ? editBlockedMsg : (linkedSources.length === 0 ? 'Add a data source first' : 'New detection rule')} data-testid="BTN_cm_rule_new" />
                        ),
                        <RulesSection ref={rulesRef} key={`rules-${selected!.id}`} collectionTypeId={selected!.id} collectionName={selected!.name} mode="define" hideRun hideCreate />,
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

      <Modal isOpen={addOpen} onClose={() => setAddOpen(false)} size="sm" closeOnOverlayClick closeOnEscape>
        <Modal.Header>{`Add Data Source — “${selected?.name ?? ''}”`}</Modal.Header>
        <Modal.Body>
          {(() => {
            const unlinked = availableSources.filter(ds => !draftIds.includes(ds.id));
            if (unlinked.length === 0) return (
              <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
                {availableSources.length === 0 ? 'No data sources — create one under Data Sources first.' : 'All data sources already added.'}
              </p>
            );
            return (
              <FormControl.SearchSelect
                label=""
                noMargin
                value={pendingSourceId}
                onChange={(val) => setPendingSourceId(val ? String(val) : '')}
                options={unlinked.map(ds => ({ value: ds.id, label: ds.name }))}
                placeholder="Select data source…"
                data-testid="SEL_cm_pipeline_sources"
              />
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="fill"
            onClick={async () => {
              const nextIds = pendingSourceId && !draftIds.includes(pendingSourceId)
                ? [...draftIds, pendingSourceId]
                : draftIds;
              setDraftIds(nextIds);
              await saveLinks(nextIds);   // explicit ids — avoid the stale-draftIds PUT
              setAddOpen(false);
            }}
            disabled={savingLinks || !pendingSourceId}
            data-testid="BTN_cm_pipeline_sources_done"
          >
            {savingLinks ? 'Saving…' : 'Add'}
          </Button>
        </Modal.Footer>
      </Modal>

      {showSourceCrud && (
        <CmPipelineEditorModal
          isOpen={editorOpen}
          mode={isDataSources ? 'data_source' : 'pipeline'}
          pipeline={isDataSources ? null : editing}
          sourceToEdit={isDataSources ? editingSource : null}
          noun={isRules ? 'Pipeline' : 'Data Source'}
          onClose={() => setEditorOpen(false)}
          onDelete={isDataSources
            ? (editingSource ? async () => { const ok = await handleDeleteSource(); if (ok) setEditorOpen(false); } : undefined)
            : (editing ? async () => { const ok = await handleDeletePipeline(); if (ok) setEditorOpen(false); } : undefined)
          }
          onSaved={handleSaved}
          onSourceSaved={handleSourceSaved}
          onOpenAggregationBuilder={() => setAggBuilderOpen(true)}
        />
      )}

      {/* Aggregation builder — opened from the Aggregation source-type card */}
      <AggregationBuilderModal
        isOpen={aggBuilderOpen}
        onClose={() => setAggBuilderOpen(false)}
        onSaved={() => { setAggBuilderOpen(false); loadSources(); }}
      />

      {/* Aggregation builder — opened from the data-source Aggregation Outputs panel */}
      <AggregationBuilderModal
        isOpen={sourceAggBuilderOpen}
        onClose={() => { setSourceAggBuilderOpen(false); setEditingSourceAgg(null); }}
        onSaved={() => {
          setSourceAggBuilderOpen(false);
          setEditingSourceAgg(null);
          if (selectedSource) loadSourceAggs(selectedSource.id);
        }}
        onDelete={editingSourceAgg ? async () => {
          await deleteSourceAgg(editingSourceAgg);
          setSourceAggBuilderOpen(false);
          setEditingSourceAgg(null);
        } : undefined}
        aggregation={editingSourceAgg}
        initialDataSourceId={selectedSource ? String(selectedSource.id) : undefined}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default CmRuleGovernance;
